import React, { useState, useEffect } from 'react';

// Built-in dashboard structure. Each "group" is a button on the Legal Documents
// dashboard; within a group are collapsible sections, and within each section
// are categories that hold uploaded files. Clients can add their own groups,
// sections, and categories, and hide/delete ones they don't need (persisted
// separately via /legal-structure).
const BUILTIN_GROUPS = [
  {
    key: 'companyFormation',
    title: 'Company Formation Documents',
    sections: [
      {
        key: 'formationDocuments',
        title: 'Formation Documents',
        categories: [
          'Charter',
          'Bylaws',
          'Initial Action of Sole Incorporator',
          'Initial Action of Board of Directors',
          'Initial Action of Stockholders',
          'Restricted Stock Purchase Agreement'
        ]
      }
    ]
  },
  {
    key: 'personnel',
    title: 'Personnel Templates & Documents',
    sections: [
      {
        key: 'templates',
        title: 'Templates',
        categories: [
          'FTE Offer Letter with options',
          'FTE Offer Letter without options',
          'Promotion Letter',
          'NDA Agreement (Employee)',
          'IP Agreement (Employee)',
          'Significant Financial Agreement'
        ]
      },
      {
        key: 'signedDocuments',
        title: 'Signed Documents',
        categories: [
          'FTE Offer Letter with options',
          'FTE Offer Letter without options',
          'Promotion Letter',
          'NDA Agreement (Employee)',
          'IP Agreement (Employee)',
          'Significant Financial Agreement'
        ]
      }
    ]
  },
  {
    key: 'ndaMta',
    title: 'NDA & MTA Templates & Documents',
    sections: [
      { key: 'templates', title: 'Templates', categories: ['NDA', 'MTA'] },
      { key: 'signedDocuments', title: 'Signed Documents', categories: ['NDA', 'MTA'] }
    ]
  },
  {
    key: 'ipTrademarks',
    title: 'Intellectual Property and Trademarks',
    sections: [
      { key: 'registeredTrademarks', title: 'Registered Trademarks', categories: ['Registered Trademarks'] },
      { key: 'patents', title: 'Patents', categories: ['Patents'] }
    ]
  },
  {
    key: 'licensing',
    title: 'Licensing and Partnership Agreements',
    sections: []
  }
];

const emptyStructure = { groups: [], sections: [], categories: [], hidden: { groups: [], sections: [], categories: [] } };
const uid = () => `c-${Math.random().toString(36).slice(2)}`;
const sectionComposite = (groupKey, sectionKey) => `${groupKey}::${sectionKey}`;
const categoryComposite = (groupKey, sectionKey, name) => `${groupKey}::${sectionKey}::${name}`;

const LegalDocumentsPage = () => {
  const [files, setFiles] = useState([]);
  const [fileUploadLimit] = useState(25 * 1024 * 1024); // 25MB
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewDocName, setPreviewDocName] = useState(null);

  const [structure, setStructure] = useState(emptyStructure);
  const [activeGroupKey, setActiveGroupKey] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  // Per-category file list expand/collapse (default collapsed, like Registrations)
  const [expandedCategories, setExpandedCategories] = useState({});
  const toggleSection = (id) => setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));

  // Modal state for adding a button / section / category
  const [modal, setModal] = useState(null); // { type: 'group'|'section'|'category', groupKey?, sectionKey? }
  const [modalName, setModalName] = useState('');

  const fetchFiles = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/legal-documents`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      setFiles(data);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const fetchStructure = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/legal-structure`, {
        method: 'GET',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStructure({
          groups: data.groups || [],
          sections: data.sections || [],
          categories: data.categories || [],
          hidden: {
            groups: data.hidden?.groups || [],
            sections: data.hidden?.sections || [],
            categories: data.hidden?.categories || []
          }
        });
      }
    } catch (error) {
      console.error('Error fetching legal structure:', error);
    }
  };

  useEffect(() => {
    fetchFiles();
    fetchStructure();
  }, []);

  // Persist a new structure to the backend and update local state.
  const persistStructure = async (next) => {
    setStructure(next);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/legal-structure`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (!response.ok) {
        alert('Failed to save changes');
      }
    } catch (error) {
      console.error('Error saving legal structure:', error);
      alert('Failed to save changes');
    }
  };

  // ---- Derived structure (built-ins + custom, minus hidden) ----
  const getGroups = () => {
    const builtIns = BUILTIN_GROUPS
      .filter(g => !structure.hidden.groups.includes(g.key))
      .map(g => ({ key: g.key, title: g.title, isCustom: false }));
    const customs = structure.groups.map(g => ({ key: g.id, title: g.title, isCustom: true }));
    return [...builtIns, ...customs];
  };

  const getSections = (groupKey) => {
    const builtinGroup = BUILTIN_GROUPS.find(g => g.key === groupKey);
    const builtIns = (builtinGroup?.sections || [])
      .filter(s => !structure.hidden.sections.includes(sectionComposite(groupKey, s.key)))
      .map(s => ({ key: s.key, title: s.title, isCustom: false }));
    const customs = structure.sections
      .filter(s => s.groupKey === groupKey)
      .map(s => ({ key: s.id, title: s.title, isCustom: true }));
    return [...builtIns, ...customs];
  };

  const getCategories = (groupKey, sectionKey) => {
    const builtinGroup = BUILTIN_GROUPS.find(g => g.key === groupKey);
    const builtinSection = builtinGroup?.sections?.find(s => s.key === sectionKey);
    const builtIns = (builtinSection?.categories || [])
      .filter(name => !structure.hidden.categories.includes(categoryComposite(groupKey, sectionKey, name)))
      .map(name => ({ name, isCustom: false }));
    const customs = structure.categories
      .filter(c => c.groupKey === groupKey && c.sectionKey === sectionKey)
      .map(c => ({ name: c.name, isCustom: true, categoryId: c.id }));
    return [...builtIns, ...customs];
  };

  const getFilesByCategory = (groupKey, sectionKey, category) =>
    files.filter(file =>
      file.documentSection === sectionComposite(groupKey, sectionKey) && file.documentType === category
    );

  // Files uploaded under the previous (2-level) structure, so nothing is lost.
  const legacyFiles = files.filter(f => !String(f.documentSection || '').includes('::'));

  // ---- Add handlers ----
  const openAdd = (type, groupKey, sectionKey) => {
    setModal({ type, groupKey, sectionKey });
    setModalName('');
  };

  const submitAdd = async () => {
    const name = modalName.trim();
    if (!name) { alert('Name is required'); return; }
    if (modal.type === 'group') {
      await persistStructure({ ...structure, groups: [...structure.groups, { id: uid(), title: name }] });
    } else if (modal.type === 'section') {
      await persistStructure({
        ...structure,
        sections: [...structure.sections, { id: uid(), groupKey: modal.groupKey, title: name }]
      });
    } else if (modal.type === 'category') {
      const exists = getCategories(modal.groupKey, modal.sectionKey).some(c => c.name.toLowerCase() === name.toLowerCase());
      if (exists) { alert('That category already exists in this section'); return; }
      await persistStructure({
        ...structure,
        categories: [...structure.categories, { id: uid(), groupKey: modal.groupKey, sectionKey: modal.sectionKey, name }]
      });
    }
    setModal(null);
    setModalName('');
  };

  // ---- Delete handlers ----
  const deleteGroup = async (group) => {
    if (!window.confirm(`Delete the "${group.title}" button? Uploaded files are not removed.`)) return;
    if (group.isCustom) {
      await persistStructure({
        ...structure,
        groups: structure.groups.filter(g => g.id !== group.key),
        sections: structure.sections.filter(s => s.groupKey !== group.key),
        categories: structure.categories.filter(c => c.groupKey !== group.key)
      });
    } else {
      await persistStructure({
        ...structure,
        hidden: { ...structure.hidden, groups: [...structure.hidden.groups, group.key] }
      });
    }
    setActiveGroupKey(null);
  };

  const deleteSection = async (groupKey, section) => {
    if (!window.confirm(`Delete the "${section.title}" section? Uploaded files are not removed.`)) return;
    if (section.isCustom) {
      await persistStructure({
        ...structure,
        sections: structure.sections.filter(s => s.id !== section.key),
        categories: structure.categories.filter(c => !(c.groupKey === groupKey && c.sectionKey === section.key))
      });
    } else {
      await persistStructure({
        ...structure,
        hidden: { ...structure.hidden, sections: [...structure.hidden.sections, sectionComposite(groupKey, section.key)] }
      });
    }
  };

  const deleteCategory = async (groupKey, sectionKey, category) => {
    if (!window.confirm(`Delete the "${category.name}" category? Uploaded files are not removed.`)) return;
    if (category.isCustom) {
      await persistStructure({
        ...structure,
        categories: structure.categories.filter(c => c.id !== category.categoryId)
      });
    } else {
      await persistStructure({
        ...structure,
        hidden: { ...structure.hidden, categories: [...structure.hidden.categories, categoryComposite(groupKey, sectionKey, category.name)] }
      });
    }
  };

  // ---- File handlers (reuse existing endpoints) ----
  const handleUpload = async (groupKey, sectionKey, category, file) => {
    if (!file) return;
    if (file.size > fileUploadLimit) {
      alert(`File size exceeds the limit of ${fileUploadLimit / (1024 * 1024)} MB.`);
      return;
    }
    try {
      const fileName = `legal/${groupKey}/${sectionKey}/${category}/${file.name}`;
      const contentType = file.type || 'application/octet-stream';

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/generate-upload-url`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName, contentType }),
      });
      if (!response.ok) throw new Error(`Failed to generate upload URL: ${await response.text()}`);
      const { url } = await response.json();

      const uploadResponse = await fetch(url, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
      if (!uploadResponse.ok) throw new Error(`Failed to upload file: ${await uploadResponse.text()}`);

      await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/update-legal-document-metadata`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName,
          documentType: category,
          documentSection: sectionComposite(groupKey, sectionKey),
          documentNumber: '',
          contentType,
          originalName: file.name
        })
      });

      alert('File uploaded successfully!');
      await fetchFiles();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file: ' + error.message);
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/generate-download-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ fileName: file.documentName }),
      });
      if (!response.ok) throw new Error(`Failed to generate download URL: ${await response.text()}`);
      const { url } = await response.json();

      const originalName = file.originalName || file.documentName.split('/').pop();
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file: ' + error.message);
    }
  };

  const handlePreview = async (documentName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/generate-preview-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ fileName: documentName }),
      });
      const { url } = await response.json();
      setPreviewUrl(url);
      setPreviewDocName(documentName);
    } catch (error) {
      console.error('Error previewing file:', error);
      alert('Error previewing file.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const fileCountForGroup = (groupKey) => {
    let count = 0;
    getSections(groupKey).forEach(sec => {
      getCategories(groupKey, sec.key).forEach(cat => {
        count += getFilesByCategory(groupKey, sec.key, cat.name).length;
      });
    });
    return count;
  };

  const renderFilesTable = (categoryFiles) => (
    <div className="border-t border-gray-200 overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">File</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody>
          {categoryFiles.map((file, idx) => (
            <tr key={idx}>
              <td className="px-4 py-3 text-sm text-gray-900">{file.originalName || file.documentName.split('/').pop()}</td>
              <td className="px-4 py-3 text-sm text-gray-500">{formatDate(file.lastModified)}</td>
              <td className="px-4 py-3 text-center">
                <div className="flex items-center justify-center gap-3">
                  <button onClick={() => handlePreview(file.documentName)} className="text-blue-600 hover:text-blue-700 text-sm">View</button>
                  <button onClick={() => handleDownload(file)} className="text-blue-600 hover:text-blue-700 text-sm">Download</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderPreview = (categoryFiles) => (
    previewUrl && previewDocName && categoryFiles.some(f => f.documentName === previewDocName) && (
      <div className="bg-white rounded-lg shadow-sm border border-blue-200 p-6 mt-3">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
          <button onClick={() => { setPreviewUrl(''); setPreviewDocName(null); }} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {['jpg', 'jpeg', 'png', 'gif'].includes(previewUrl.split('.').pop()?.split('?')[0]?.toLowerCase()) ? (
          <img src={previewUrl} alt="Document Preview" className="max-w-full max-h-[500px] mx-auto" />
        ) : (
          <iframe src={previewUrl} title="Document Preview" className="w-full h-[500px] border border-gray-200 rounded" />
        )}
      </div>
    )
  );

  const activeGroup = activeGroupKey
    ? getGroups().find(g => g.key === activeGroupKey)
    : null;

  // ---- Dashboard (button grid) ----
  if (!activeGroup) {
    const groups = getGroups();
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Legal Documents</h1>
          <p className="text-gray-600 mt-2">Select a category to view and manage documents</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map(group => (
            <div
              key={group.key}
              onClick={() => { setActiveGroupKey(group.key); setPreviewUrl(''); setPreviewDocName(null); }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all relative group"
            >
              <button
                onClick={(e) => { e.stopPropagation(); deleteGroup(group); }}
                className="absolute top-2 right-2 text-gray-300 hover:text-red-500 text-sm opacity-0 group-hover:opacity-100"
                title="Delete button"
              >
                ✕
              </button>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{group.title}</h3>
              <p className="text-sm text-gray-500 mt-1">{fileCountForGroup(group.key)} file(s)</p>
            </div>
          ))}

          {/* Add Button tile */}
          <button
            onClick={() => openAdd('group')}
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-all"
          >
            <span className="text-3xl leading-none">+</span>
            <span className="mt-2 text-sm font-medium">Add Button</span>
          </button>
        </div>

        {renderAddModal()}
      </div>
    );
  }

  // ---- Group detail view ----
  const sections = getSections(activeGroup.key);
  return (
    <div className="max-w-6xl mx-auto p-6">
      <button
        onClick={() => { setActiveGroupKey(null); setPreviewUrl(''); setPreviewDocName(null); }}
        className="text-blue-600 hover:text-blue-700 text-sm mb-4 flex items-center gap-1"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Legal Documents
      </button>

      <div className="mb-6 flex justify-between items-start">
        <h1 className="text-3xl font-bold text-gray-900">{activeGroup.title}</h1>
        <button
          onClick={() => openAdd('section', activeGroup.key)}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + Add Section
        </button>
      </div>

      {sections.length === 0 && (
        <p className="text-gray-500 mb-6">No sections yet. Click "+ Add Section" to create one.</p>
      )}

      {sections.map(section => {
        const sectionKey = sectionComposite(activeGroup.key, section.key);
        const isCollapsed = !expandedSections[sectionKey];
        const categories = getCategories(activeGroup.key, section.key);
        return (
          <div key={section.key} className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Section header (collapsible) */}
            <div className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50" onClick={() => toggleSection(sectionKey)}>
              <div className="flex items-center gap-3">
                <span className="text-gray-400 text-sm">{isCollapsed ? '▶' : '▼'}</span>
                <h2 className="text-lg font-semibold text-gray-900">{section.title}</h2>
                <span className="text-xs text-gray-400">{categories.length} categor{categories.length === 1 ? 'y' : 'ies'}</span>
              </div>
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => openAdd('category', activeGroup.key, section.key)}
                  className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
                >
                  + Add Category
                </button>
                <button
                  onClick={() => deleteSection(activeGroup.key, section)}
                  className="px-3 py-1.5 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                  title="Delete section"
                >
                  Delete
                </button>
              </div>
            </div>

            {!isCollapsed && (
              <div className="border-t border-gray-200 p-4 space-y-3">
                {categories.length === 0 && (
                  <p className="text-gray-500 text-sm">No categories yet. Click "+ Add Category".</p>
                )}
                {categories.map(cat => {
                  const categoryFiles = getFilesByCategory(activeGroup.key, section.key, cat.name);
                  const categoryKey = `${sectionKey}::${cat.name}`;
                  const isCategoryCollapsed = !expandedCategories[categoryKey];
                  const canCollapse = categoryFiles.length > 0;
                  return (
                    <div key={cat.name} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                      <div
                        className={`flex justify-between items-center p-3 ${canCollapse ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                        onClick={() => canCollapse && setExpandedCategories(prev => ({ ...prev, [categoryKey]: !prev[categoryKey] }))}
                      >
                        <div className="flex items-center gap-3">
                          {canCollapse && (
                            <span className="text-gray-400 text-sm">{isCategoryCollapsed ? '▶' : '▼'}</span>
                          )}
                          <h3 className="font-medium text-gray-900">{cat.name}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                            categoryFiles.length > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {categoryFiles.length > 0 ? `${categoryFiles.length} file${categoryFiles.length === 1 ? '' : 's'}` : 'Empty'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <label className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
                            Upload
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleUpload(activeGroup.key, section.key, cat.name, e.target.files[0]);
                                  e.target.value = '';
                                }
                              }}
                            />
                          </label>
                          <button
                            onClick={() => deleteCategory(activeGroup.key, section.key, cat)}
                            className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
                            title="Delete category"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      {categoryFiles.length > 0 && !isCategoryCollapsed && renderFilesTable(categoryFiles)}
                      {renderPreview(categoryFiles)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Legacy documents uploaded before the dashboard restructure */}
      {activeGroup.key === 'companyFormation' && legacyFiles.length > 0 && (
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-amber-200 overflow-hidden">
          <div className="p-4 border-b border-amber-100 bg-amber-50">
            <h2 className="text-lg font-semibold text-gray-900">Previously Uploaded Documents</h2>
            <p className="text-xs text-gray-500 mt-1">Files uploaded before the dashboard was reorganized.</p>
          </div>
          {renderFilesTable(legacyFiles)}
          {renderPreview(legacyFiles)}
        </div>
      )}

      {renderAddModal()}
    </div>
  );

  function renderAddModal() {
    if (!modal) return null;
    const titles = { group: 'Add Button', section: 'Add Section', category: 'Add Category' };
    const labels = { group: 'Button Name', section: 'Section Name', category: 'Category Name' };
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{titles[modal.type]}</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{labels[modal.type]} *</label>
              <input
                type="text"
                value={modalName}
                onChange={(e) => setModalName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitAdd(); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                autoFocus
              />
            </div>
            <div className="flex space-x-2 pt-2">
              <button onClick={submitAdd} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex-1">
                Create
              </button>
              <button
                onClick={() => { setModal(null); setModalName(''); }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default LegalDocumentsPage;
